const getAnalyticsEventCenter = require('../../../common/getAnalyticsEventCenter');
const {
    HashmapStorageFactory,
    HASHMAP_STORAGE_TYPE
} = require('../../../utilities/hashmapStorage');
const {
    getContactKeyForEmailAddress,
    createTemporaryContact,
    updateTemporaryContact
} = require('../../../utilities/sfmcHelpers');

const analyticsEventCenter = getAnalyticsEventCenter();
const KEYS = {
    /**
     * @type {string}
     * Customer contact key in Salesforce Marketing Cloud
     */
    customerContactKey: 'contactKey',

    /** @type {string} Customer email address */
    customerEmail: 'email',

    /**
     * @type {string}
     * Indicates if the customer was influenced by onsite content
     */
    isOnsiteInfluencedCustomer: 'isInfluenced',

    /**
     * @type {string}
     * Indicates if the visitor came from an email
     */
    isVisitorFromEmail: 'isEmailVisitor',

    /**
     * @type {string}
     * Indicates if the user is authenticated in the session
     */
    isLoggedIn: 'isLoggedIn',

    /**
     * @type {string}
     * Indicates if the user is an active SFMC subscriber.
     * false includes users who rejected marketing communications
     */
    isSubscriber: 'isSubscriber'
};

const storage = HashmapStorageFactory.createHashmapStorage({
    type: HASHMAP_STORAGE_TYPE.SESSION_STORAGE,
    name: 'abandonedCartCustomerSession'
});

/**
 * Get the UID of the current customer.
 * The UID is Contact Key from SFMC.
 * If the user is not tracked, return null.
 * @returns {string|null} The current customer UID
 */
function getCustomerContactKey() {
    return storage.getItem(KEYS.customerContactKey);
}

/**
 * Return true if the system cannot relate the events to a target user
 * @returns {boolean} True if it is anonymous; otherwise, false
 */
function isAnonymousCustomer() {
    return storage.getItem(KEYS.isLoggedIn) !== true;
}

/**
 * Return true if the system cannot relate the events to a target user
 * @returns {boolean} True if it is anonymous; otherwise, false
 */
function isOnsiteInfluencedCustomer() {
    return storage.getItem(KEYS.isOnsiteInfluencedCustomer) === true;
}

/**
 * Returns whether the visitor originated from an email link.
 *
 * This checks the persisted `isVisitorFromEmail` flag in storage.
 *
 * @returns {boolean} `true` if the visitor came from an email; otherwise `false`.
 */
function isVisitorFromEmail() {
    return storage.getItem(KEYS.isVisitorFromEmail) === true;
}

/**
 * Marks the current visitor as coming from an email campaign.
 *
 * This function sets an internal session flag to indicate that the visitor
 * arrived via an email link. It does not require any parameters and does
 * not return a value. The actual storage mechanism (session, cookie, or
 * other) is abstracted away and should not concern the caller.
 *
 * Use in conjunction with `isVisitorFromEmail()` to check the visitor's state.
 *
 * Example:
 *   if (!session.isVisitorFromEmail()) {
 *       session.markVisitorFromEmail();
 *   }
 *
 * @function
 */
function markVisitorFromEmail() {
    storage.setItem(KEYS.isVisitorFromEmail, true);
}

/**
 * Init tracking
 */
function init() {
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.abandonedCartModalContactReady,
        /**
         * Handle the event triggered after submitting the abandoned cart modal
         * form, once a valid contact has been resolved in SFMC (either newly
         * created or retrieved if it already existed).
         *
         * This event guarantees that a contactKey is available and
         * associated with the provided email.
         *
         * @param {Object} payload The payload of the event
         * @param {string} payload.contactKey The unique identifier of the related
         *   contact
         * @param {string} payload.email The email address provided by the guest
         *   user
         */
        (payload) => {
            storage.setItem(KEYS.customerEmail, payload.email);
            storage.setItem(KEYS.customerContactKey, payload.contactKey);
        }
    );

    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.userLoggedIn,
        /**
         * Handle the userLoggedIn event
         * @param {Object} payload The payload of the event
         * @param {Object} payload.authenticatedCustomer The customer info object
         * @param {string} payload.authenticatedCustomer.ID The ID of the customer
         * @param {string} payload.authenticatedCustomer.email The email of the
         *  just logged customer
         */
        async (payload) => {
            storage.setItem(KEYS.isLoggedIn, true);
            const email = payload.authenticatedCustomer.email;
            let contactKey = await getContactKeyForEmailAddress(email);
            if (!contactKey) {
                // No contact exists, create new one for logged user that is not
                // a newsletter / commercial subscriber
                storage.setItem(KEYS.isSubscriber, false);
                const response = await createTemporaryContact(email, true);
                if (response.ok && response.contactKey) {
                    contactKey = response.contactKey;
                }
            } else if (contactKey.startsWith('abcart_')) {
                // Temporary contact exists. Update DE in case IsRegisteredSfccUser
                // has changed (e.g. guest who has now logged in as registered user).
                storage.setItem(KEYS.isSubscriber, false);
                updateTemporaryContact(email, true);
            } else {
                // Real subscriber contact exists
                storage.setItem(KEYS.isSubscriber, true);
            }
            storage.setItem(KEYS.customerEmail, email);
            storage.setItem(KEYS.customerContactKey, contactKey);
        },
        true
    );

    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.abandonedCartModalInteracted,
        /**
         * Track the interaction with any abandoned cart modal.
         *
         * @param {Object} payload The payload of the event
         * @param {string} payload.interaction The type of interaction
         * @param {string} payload.modalType The type of the modal (consent or save
         * my basket for guest users)
         */
        ({ interaction } = {}) => {
            const payloads = analyticsEventCenter.PAYLOADS.abandonedCartModalInteracted;
            const isPositiveInteraction = interaction === payloads.interaction.continueShopping
                || interaction === payloads.interaction.submitEmailSuccess;
            if (isPositiveInteraction) {
                storage.setItem(KEYS.isOnsiteInfluencedCustomer, true);
            }
        }
    );
}

module.exports = {
    getCustomerContactKey,
    isOnsiteInfluencedCustomer,
    isVisitorFromEmail,
    init,
    isAnonymousCustomer,
    markVisitorFromEmail
};
